import { utcNow } from "@dashboard-config"
import type { CalendarEventsResponse } from "@site-types/calendar"

const PROD_EVENTS_ENDPOINT = "https://events-api.playbrain.io/api/"
const FALLBACK_STAGE_EVENTS_ENDPOINTS: Record<string, string> = {
  dev: "https://dev.events-api.playbrain.io/api/",
  staging: "https://staging.events-api.playbrain.io/api/",
  prod: PROD_EVENTS_ENDPOINT,
  production: PROD_EVENTS_ENDPOINT,
}
const START_DATE = "1990-01-01"
export const CALENDAR_FETCH_TIMEOUT_MS = 3000

const mockCalendarEvents: CalendarEventsResponse = {
  totalCount: 4,
  results: [
    {
      name: "PLAYBRAIN Calendar Event",
      taiyoroUrl: "https://taiyoro.gg/",
      imageUrl: "/images/playbrain_symbol_outline.svg",
    },
    {
      name: "TAIYORO Event",
      taiyoroUrl: "https://taiyoro.gg/",
      imageUrl: "/images/taiyoro-logo.svg",
    },
    {
      name: "FLUX Event",
      taiyoroUrl: "https://flux.jp/",
      imageUrl: "/images/flux-logo.svg",
    },
    {
      name: "PLAYDATA Event",
      taiyoroUrl: "https://playdata.gg/",
      imageUrl: "/images/playdata-logo.svg",
    },
  ],
}

const emptyCalendarEvents: CalendarEventsResponse = {
  totalCount: 0,
  results: [],
}

type CalendarEndpointEnv = Pick<
  ImportMetaEnv,
  | "PUBLIC_DEV_EVENTS_ENDPOINT"
  | "PUBLIC_STAGING_EVENTS_ENDPOINT"
  | "PUBLIC_PROD_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_DEV_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_STAGING_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_PROD_EVENTS_ENDPOINT"
>

export function shouldUseCalendarMockFallback(stage = import.meta.env.PUBLIC_STAGE): boolean {
  return !stage || stage === "local" || stage === "dev"
}

export function calendarFallbackEvents(stage = import.meta.env.PUBLIC_STAGE): CalendarEventsResponse {
  return shouldUseCalendarMockFallback(stage) ? mockCalendarEvents : emptyCalendarEvents
}

function calendarEndDate() {
  return utcNow().add(1, "year").format("YYYY-MM-DD")
}

function configuredStageEventsEndpoint(stage: string, env: CalendarEndpointEnv): string | undefined {
  if (stage === "dev") return env.PUBLIC_DEV_EVENTS_ENDPOINT || env.VITE_PUBLIC_DEV_EVENTS_ENDPOINT
  if (stage === "staging")
    return env.PUBLIC_STAGING_EVENTS_ENDPOINT || env.VITE_PUBLIC_STAGING_EVENTS_ENDPOINT
  if (stage === "prod" || stage === "production")
    return env.PUBLIC_PROD_EVENTS_ENDPOINT || env.VITE_PUBLIC_PROD_EVENTS_ENDPOINT
  return undefined
}

export function calendarEventsEndpointCandidates(
  stage = import.meta.env.PUBLIC_STAGE,
  configuredEndpoint = import.meta.env.PUBLIC_EVENTS_ENDPOINT,
  endpointEnv: CalendarEndpointEnv = import.meta.env
): string[] {
  const normalizedStage = stage || ""
  const productionEndpoint =
    configuredStageEventsEndpoint("prod", endpointEnv)?.trim() || PROD_EVENTS_ENDPOINT
  const endpoint =
    configuredEndpoint?.trim() ||
    configuredStageEventsEndpoint(normalizedStage, endpointEnv)?.trim() ||
    FALLBACK_STAGE_EVENTS_ENDPOINTS[normalizedStage] ||
    productionEndpoint
  return Array.from(new Set([endpoint, productionEndpoint]))
}

function eventsUrl(base: string) {
  const url = new URL("query", base.endsWith("/") ? base : `${base}/`)
  url.search = new URLSearchParams({
    resource: "feedEvent",
    activeFrom: START_DATE,
    activeTo: calendarEndDate(),
    companyName: "Playbrain",
    limit: "500",
    offset: "0",
  }).toString()
  return url
}

function isCalendarEventsResponse(value: unknown): value is CalendarEventsResponse {
  if (!value || typeof value !== "object") return false
  const candidate = value as Partial<CalendarEventsResponse>
  return typeof candidate.totalCount === "number" && Array.isArray(candidate.results)
}

export async function getCalendarEvents(
  stage = import.meta.env.PUBLIC_STAGE,
  fetchImpl: typeof fetch = fetch,
  configuredEndpoint = import.meta.env.PUBLIC_EVENTS_ENDPOINT,
  endpointEnv: CalendarEndpointEnv = import.meta.env
): Promise<CalendarEventsResponse> {
  for (const endpoint of calendarEventsEndpointCandidates(stage, configuredEndpoint, endpointEnv)) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CALENDAR_FETCH_TIMEOUT_MS)
    try {
      const response = await fetchImpl(eventsUrl(endpoint), { signal: controller.signal })
      if (!response.ok) {
        console.error(`Calendar events request to ${endpoint} failed with status ${response.status}`)
        continue
      }

      const events = await response.json()
      if (!isCalendarEventsResponse(events)) {
        console.error(`Calendar events response from ${endpoint} did not match the expected shape`)
        continue
      }

      if (events.totalCount > 0) return events
      console.error(`Calendar events response from ${endpoint} did not include any records`)
    } catch (error) {
      console.error(`Calendar events request to ${endpoint} failed`, error)
    } finally {
      clearTimeout(timeout)
    }
  }

  return calendarFallbackEvents(stage)
}
